import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  Bug, AlertCircle, CheckCircle, Clock, Filter,
  ChevronLeft, ChevronRight, User, Calendar, ExternalLink
} from 'lucide-react';
import { adminFetch } from './AdminLayout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const statusColors = {
  open: 'bg-red-600/20 text-red-400',
  in_progress: 'bg-yellow-600/20 text-yellow-400',
  resolved: 'bg-green-600/20 text-green-400',
  closed: 'bg-gray-600/20 text-gray-400',
};

const statusIcons = {
  open: AlertCircle,
  in_progress: Clock,
  resolved: CheckCircle,
  closed: CheckCircle,
};

const AdminBugs = () => {
  const { adminToken } = useOutletContext();
  const [bugs, setBugs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedBug, setSelectedBug] = useState(null);
  const [updating, setUpdating] = useState(false);
  const limit = 20;

  const fetchBugs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * limit, limit });
      if (statusFilter) params.append('status', statusFilter);
      
      const data = await adminFetch(`/admin/bug-reports?${params}`, {}, adminToken);
      setBugs(data.bug_reports);
      setTotal(data.total);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBugs();
  }, [page, statusFilter, adminToken]);

  const updateBugStatus = async (reportId, newStatus, notes = '') => {
    setUpdating(true);
    try {
      await adminFetch(`/admin/bug-reports/${reportId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus, admin_notes: notes })
      }, adminToken);
      
      toast.success('Bug status updated');
      fetchBugs();
      setSelectedBug(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Bug Reports</h1>
          <p className="text-gray-400 mt-1">{total} total reports</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <Filter size={18} className="text-gray-400" />
        {['', 'open', 'in_progress', 'resolved', 'closed'].map((status) => (
          <button
            key={status}
            onClick={() => { setStatusFilter(status); setPage(0); }}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              statusFilter === status
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {status || 'All'}
          </button>
        ))}
      </div>

      {/* Bug List */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : bugs.length === 0 ? (
          <div className="p-12 text-center">
            <Bug className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No bug reports found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {bugs.map((bug) => {
              const StatusIcon = statusIcons[bug.status] || AlertCircle;
              return (
                <div 
                  key={bug.report_id}
                  className="p-4 hover:bg-gray-700/30 cursor-pointer transition-colors"
                  onClick={() => setSelectedBug(bug)}
                  data-testid={`bug-row-${bug.report_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 ${statusColors[bug.status]}`}>
                          <StatusIcon size={12} />
                          {bug.status}
                        </span>
                        <h3 className="text-white font-medium">{bug.title}</h3>
                      </div>
                      <p className="text-gray-400 text-sm mt-1 line-clamp-2">{bug.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        {bug.user_info && (
                          <span className="flex items-center gap-1">
                            <User size={12} />
                            {bug.user_info.name || bug.user_info.email}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {new Date(bug.created_at).toLocaleDateString()}
                        </span>
                        {bug.page_url && (
                          <span className="flex items-center gap-1">
                            <ExternalLink size={12} />
                            {bug.page_url}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-400">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p - 1)}
                disabled={page === 0}
                className="border-gray-600"
              >
                <ChevronLeft size={16} />
              </Button>
              <span className="text-gray-400 text-sm px-2">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
                className="border-gray-600"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bug Detail Modal */}
      {selectedBug && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Bug Report Details</h3>
              <button onClick={() => setSelectedBug(null)} className="text-gray-400 hover:text-white">
                ×
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm text-gray-400">Title</label>
                <p className="text-white text-lg">{selectedBug.title}</p>
              </div>
              
              <div>
                <label className="text-sm text-gray-400">Description</label>
                <p className="text-gray-200 whitespace-pre-wrap">{selectedBug.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">Status</label>
                  <p className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${statusColors[selectedBug.status]}`}>
                    {selectedBug.status}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Reported By</label>
                  <p className="text-white">{selectedBug.user_info?.email || 'Anonymous'}</p>
                </div>
                {selectedBug.page_url && (
                  <div className="col-span-2">
                    <label className="text-sm text-gray-400">Page URL</label>
                    <p className="text-blue-400">{selectedBug.page_url}</p>
                  </div>
                )}
                {selectedBug.browser_info && (
                  <div className="col-span-2">
                    <label className="text-sm text-gray-400">Browser Info</label>
                    <p className="text-gray-300 text-sm">{selectedBug.browser_info}</p>
                  </div>
                )}
              </div>

              {selectedBug.admin_notes && (
                <div>
                  <label className="text-sm text-gray-400">Admin Notes</label>
                  <p className="text-gray-200">{selectedBug.admin_notes}</p>
                </div>
              )}

              {/* Status Actions */}
              <div className="border-t border-gray-700 pt-4">
                <label className="text-sm text-gray-400 mb-2 block">Update Status</label>
                <div className="flex flex-wrap gap-2">
                  {['open', 'in_progress', 'resolved', 'closed'].map((status) => (
                    <Button
                      key={status}
                      variant={selectedBug.status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => updateBugStatus(selectedBug.report_id, status)}
                      disabled={updating || selectedBug.status === status}
                      className={selectedBug.status === status ? 'bg-emerald-600' : 'border-gray-600'}
                    >
                      {status.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBugs;
